import { CustomerAccessControl } from 'src/hooks/useCustomerAccess'

/**
 * Check if user can perform action on a customer record
 * @param accessControl - Customer access control object from useCustomerAccess hook
 * @param action - Action to check: 'create', 'edit', 'delete', or 'view'
 * @param customerId - Optional customer ID to check allocation
 * @returns boolean indicating if action is allowed
 */
export const canPerformAction = (
  accessControl: CustomerAccessControl,
  action: 'create' | 'edit' | 'delete' | 'view',
  customerId?: number | string | null
): boolean => {
  if (!accessControl) return false
  
  switch (action) {
    case 'view':
      return accessControl.canView && 
             (customerId ? accessControl.hasAccessToCustomer(customerId) : true)
    case 'create':
      return accessControl.canCreate
    case 'edit':
      return accessControl.canEdit && 
             (customerId ? accessControl.hasAccessToCustomer(customerId) : true)
    case 'delete':
      return accessControl.canDelete && 
             (customerId ? accessControl.hasAccessToCustomer(customerId) : true)
    default:
      return false
  }
}

/**
 * Get button props for action buttons based on access control
 * @param accessControl - Customer access control object
 * @param action - Action type
 * @param customerId - Optional customer ID
 * @returns Object with disabled state, style, and title props
 */
export const getActionButtonProps = (
  accessControl: CustomerAccessControl,
  action: 'create' | 'edit' | 'delete',
  customerId?: number | string | null
) => {
  if (!accessControl) {
    return {
      disabled: true,
      style: { opacity: 0.5, cursor: 'not-allowed' },
      title: 'Access control not available'
    }
  }
  
  const canPerform = canPerformAction(accessControl, action, customerId)
  
  let title = ''
  if (!canPerform) {
    if (accessControl.isReadOnly) {
      title = 'Read-only access'
    } else if (accessControl.isRestricted) {
      title = 'Access restricted'
    } else if (customerId && !accessControl.hasAccessToCustomer(customerId)) {
      title = 'No access to this customer'
    } else {
      title = 'Action not permitted'
    }
  }
  
  return {
    disabled: !canPerform,
    style: !canPerform ? { opacity: 0.5, cursor: 'not-allowed' } : undefined,
    title: title || undefined
  }
}

/**
 * Check if action button should be shown
 * @param accessControl - Customer access control object
 * @param action - Action type
 * @param customerId - Optional customer ID
 * @returns boolean indicating if button should be visible
 */
export const shouldShowAction = (
  accessControl: CustomerAccessControl,
  action: 'create' | 'edit' | 'delete',
  customerId?: number | string | null
): boolean => {
  if (!accessControl) return false
  if (accessControl.isRestricted) return false
  return canPerformAction(accessControl, action, customerId)
}

/**
 * Get form field disabled state based on access control
 * @param accessControl - Customer access control object
 * @param additionalDisabled - Additional disabled conditions
 * @returns boolean indicating if field should be disabled
 */
export const getFormFieldDisabled = (
  accessControl: CustomerAccessControl,
  additionalDisabled: boolean = false
): boolean => {
  if (!accessControl) return true
  return accessControl.isReadOnly || accessControl.isRestricted || additionalDisabled
}

/**
 * Check if user can view customer data
 * @param accessControl - Customer access control object
 * @param customerId - Optional customer ID
 * @returns boolean indicating if user can view
 */
export const canViewCustomer = (
  accessControl: CustomerAccessControl,
  customerId?: number | string | null
): boolean => {
  if (!accessControl) return false
  return canPerformAction(accessControl, 'view', customerId)
}

